
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'

// components
import Loader from '@/common/components/UI/Loader/index.vue'
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'

// module components
import ContractForm from '@/modules/ContractsModule/components/ContractForm/index.vue'

// types
import { ContractT, DocumentT } from '@/core/types/contracts.types'

// realisations
import { contracts } from '@/core/realisations/contracts.realisation'

// composable
import { useRoute, useRouter } from 'vue-router'
import { useMaska } from '@/common/composable/useMaska'
import useAccount from '@/common/composable/useAccount'
import useNotifications from '@/common/composable/useNotifications'

import { formatDateWithTimeFromBackend, formatDateFromBackend } from '@/common/helpers/formatDate'

// constants
import {
    ContractsItemColumns,
    ContractsDocumentsColumns,
} from '@/core/constants/columns/Contracts.columns'

export default defineComponent({
    components: {
        loader: Loader,
        'page-title': PageTitle,
        cheap: Cheap,
        'the-button': TheButton,
        TableScrollWrapper,
        TheTable,
        ContractForm,
    },
    setup() {
        const { maskNumberToPrice } = useMaska()
        const router = useRouter()
        const route = useRoute()

        const { havePermissions } = useAccount()
        const notifications = useNotifications()

        const contractId = Number(route.params.contract_id)

        const state = reactive({
            is_loading: true,
            is_delete_loading: false,
            is_contract_download_loading: false,
            is_contract_show_loading: false,
            is_document_loading: false,
            is_edit_contract: false,
            //
            contract: null as ContractT | null,
        })

        const checkPermissions = () => {
            if (!havePermissions(['contract-contract-archive', 'all-root-permission'])) {
                notifications.notify({
                    title: 'У вас нет доступа для просмотра этого договора',
                    type: 'danger',
                })
                router.replace('/contracts')
            }
        }

        const loadContract = async (contract_id: number) => {
            try {
                state.is_loading = true
                const response = await contracts.getContract(contract_id)

                if (response.data.type_id === '10') {
                    checkPermissions()
                }

                state.contract = response.data
            } catch (error) {
                console.error(error)
            } finally {
                state.is_loading = false
            }
        }

        const goBack = () => {
            router.go(-1)
        }

        const saveFile = (content: File | Blob, file_name: string) => {
            const download_url = window.URL.createObjectURL(content)

            const link = document.createElement('a')
            link.href = download_url
            link.setAttribute('download', file_name)
            document.body.appendChild(link)
            link.click()
            link.remove()
        }

        const showFile = (content: File | Blob) => {
            const url = window.URL.createObjectURL(content)
            window.open(url, '_blank')
        }

        const downloadContract = async () => {
            if (!state.contract) return

            try {
                state.is_contract_download_loading = true
                const response = await contracts.downloadContract(contractId)
                saveFile(response, state.contract.original_name)
            } catch (error) {
                if (Array.isArray(error) && error.length) {
                    notifications.notifyError({
                        error,
                        title: 'При скачивании возникла ошибка',
                    })
                } else {
                    notifications.notify({
                        title: 'Интересующая сторона еще не подписала документ, нельзя скачать документ',
                        type: 'danger',
                    })
                }
            } finally {
                state.is_contract_download_loading = false
            }
        }

        const showContract = async () => {
            try {
                state.is_contract_show_loading = true
                const response = await contracts.showContract(contractId)
                showFile(response)
            } catch (error) {
                console.error(error)
                if (Array.isArray(error) && error.length) {
                    notifications.notifyError({
                        error,
                        title: 'При просмотре возникла ошибка',
                    })
                } else {
                    notifications.notify({
                        title: 'Интересующая сторона еще не подписала документ, нельзя просмотреть документ',
                        type: 'danger',
                    })
                }
            } finally {
                state.is_contract_show_loading = false
            }
        }

        const downloadDocument = async (document: DocumentT) => {
            try {
                state.is_document_loading = true

                const response = await contracts.downloadDocument(document.id)
                saveFile(response, document.name)
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: `При скачивании возникла ошибка`,
                })
            } finally {
                state.is_document_loading = false
            }
        }

        const showDocument = async (document: DocumentT) => {
            try {
                state.is_document_loading = true

                const response = await contracts.showDocument(document.id)
                showFile(response)
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: `При скачивании возникла ошибка`,
                })
            } finally {
                state.is_document_loading = false
            }
        }

        const deleteContract = async () => {
            const answer = confirm(`Вы действительно хотите удалить контракт ID: ${contractId}?`)
            if (!answer) return

            try {
                state.is_loading = true

                await contracts.deleteContractById(contractId)
                notifications.notify({ title: 'Договор успешно удален!', type: 'success' })
                router.push('/contracts')
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При удалении возникла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const deleteAdditionalContract = async (id: number) => {
            const answer = confirm(`Вы действительно хотите удалить доп. договор ID: ${id}?`)
            if (!id || !answer) return

            try {
                state.is_delete_loading = true

                await contracts.deleteAdditionalContract(id)
                notifications.notify({
                    title: 'Дополнительный договор успешно удален!',
                    type: 'success',
                })
                loadContract(contractId)
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При удалении возникла ошибка',
                })
            } finally {
                state.is_delete_loading = false
            }
        }

        const onContractEdited = () => {
            state.is_edit_contract = false
            loadContract(contractId)
        }

        const getAmount = computed(() => {
            return state.contract && state.contract.amount
                ? maskNumberToPrice(state.contract.amount)
                : '-'
        })

        const getCheckOrTimesIcon = (is_check: boolean) => {
            return `fas ${is_check ? 'fa-check text-green-500 mr-4' : 'fa-times text-red-500 mr-4'}`
        }

        const getServicesStr = (services: { name: string }[]) => {
            return services.map((item) => item.name).join(', ')
        }

        onBeforeMount(() => {
            loadContract(contractId)
        })

        return {
            state,
            goBack,
            downloadContract,
            showContract,
            loadContract,
            downloadDocument,
            showDocument,
            onContractEdited,
            havePermissions,
            deleteContract,
            deleteAdditionalContract,
            getCheckOrTimesIcon,
            getServicesStr,
            //
            getAmount,
            //
            ContractsItemColumns,
            ContractsDocumentsColumns,
            contractId,
            //
            formatDateWithTimeFromBackend,
            formatDateFromBackend
        }
    },
})
